import React, { useState } from 'react';
import { Modal, FormField, NumberField, ColorSwatches, Button } from '../ui';
import { IconTrash } from '../../components/icons';
import { CREW_ROLES, SKILL_LEVELS } from '../../lib/constants';
import db from '../../lib/database';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useAuth } from '../../auth/AuthContext';

const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

const SKILL_CATEGORIES = [
    { key: 'skill_moving', label: 'Moving', icon: '🚚', color: 'red', getTitle: (lvl) => { const titles = { 1: 'New Recruit', 2: 'Rookie', 3: 'Seasoned Crew', 4: 'Jr Crew Leader', 5: 'Crew Leader' }; return titles[Number(lvl)] || ''; } },
    { key: 'skill_packing', label: 'Packing', icon: '📦', color: 'pink', getTitle: (lvl) => `Packing Lvl ${lvl}` },
];

// takenColors: hexes used by OTHER active crew members (CrewPage passes
// them) so the swatch grid can mark colors already on the board.
export default function CrewModal({ member, onClose, onSave, onDelete, onPermDelete, takenColors = [] }) {
    const normalizeRole = (role) => {
        const LEGACY_ROLE_MAP = { 'lead': 'crew_leader', 'mover': 'new_recruit', 'crew_member': 'new_recruit', 'sr_crew_lead': 'crew_leader' };
        return LEGACY_ROLE_MAP[role] || role;
    };

    const [form, setForm] = useState(member ? { ...member, role: normalizeRole(member.role) } : { name: '', display_color: '#3b82f6', role: 'new_recruit', hourly_rate: 22, phone: '', skill_level: 1, skill_moving: 0, skill_packing: 0, active: true, gets_crew_pay: true });
    const [saving, setSaving] = useState(false);
    const [resettingPin, setResettingPin] = useState(false);
    // Portal invite (email + password auth, crew-auth-plan-v1). The email
    // is typed here because crew_members has no email column — it becomes
    // the login identity on the auth account, not a roster field.
    const [inviteEmail, setInviteEmail] = useState('');
    const [sendingInvite, setSendingInvite] = useState(false);
    const { show } = useToast();
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    // Compensation data requires manage_crew capability (admin tier+). Users
    // without that can still edit names/phones/skills/colors but the rate
    // field is field-gated below.
    const { can } = useAuth();
    const canManageCrew = can('manage_crew');
    const canManageLogins = can('manage_crew_logins');
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSave = async () => {
        if (!form.name) return;
        setSaving(true);
        const topSkill = Math.max(form.skill_moving || 0, form.skill_packing || 0, 1);
        try { await onSave({ ...form, skill_level: topSkill }); onClose(); } catch(e) { show('Save failed: '+(e.message||'Unknown error'), 'error'); } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!member?.id) return;
        if (!(await confirmDialog({ title: `Deactivate ${member.name}?`, body: `${member.name} comes off the roster and can't be assigned to new jobs. Their record, skills and past jobs are kept — you can reactivate later.`, confirmLabel: 'Deactivate', danger: true }))) return;
        setSaving(true);
        try { await onDelete(member.id); onClose(); } catch(e) { show('Error: '+(e.message||'Unknown error'), 'error'); } finally { setSaving(false); }
    };

    // Admin-only: clear this crew lead's portal PIN so they can set a
    // fresh one on next /crew visit. Backed by the SQL helper
    // crew_portal_reset_pin() in sql/015_crew_portal_auth.sql, which also
    // revokes any active sessions for that member. Used when a crew lead
    // forgets their PIN — there's no self-serve recovery in v1.
    const handleResetPin = async () => {
        if (!member?.id) return;
        if (!(await confirmDialog({ title: `Reset ${member.name}'s portal PIN?`, body: `The current PIN stops working and any active portal sessions are signed out. ${member.name} will set a new PIN on their next visit.`, confirmLabel: 'Reset PIN', danger: true }))) return;
        setResettingPin(true);
        try {
            await db.resetCrewPortalPin(member.id);
            show(`PIN reset for ${member.name}.`, 'success');
        } catch (e) {
            show(`Reset failed: ${e.message}`, 'error');
        } finally {
            setResettingPin(false);
        }
    };

    // Send a portal invite — the API creates/links the auth account and
    // texts the setup link to the member's phone via RingCentral. If the
    // SMS fails the invite still stands; we surface the URL for manual
    // delivery via the toast.
    const handleSendInvite = async () => {
        if (!member?.id) return;
        const email = inviteEmail.trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            show('Enter a valid email address first.', 'error');
            return;
        }
        if (!member.phone && !form.phone) {
            show(`${member.name} needs a phone number on file first.`, 'error');
            return;
        }
        setSendingInvite(true);
        try {
            const result = await db.sendCrewPortalInvite(member.id, email);
            if (result.sms_sent) {
                show(`Setup link texted to ${member.name}.`, 'success');
            } else if (result.needs_setup === false) {
                show(`${member.name} already has a staff login — portal access linked, no setup needed.`, 'success');
            } else {
                // Invite created but SMS failed — give the office the link.
                await navigator.clipboard?.writeText(result.setup_url).catch(() => {});
                show(`Invite created but the text failed (${result.sms_error || 'SMS error'}). Setup link copied to clipboard.`, 'warning');
            }
            setInviteEmail('');
        } catch (e) {
            show(`Invite failed: ${e.message}`, 'error');
        } finally {
            setSendingInvite(false);
        }
    };

    const skillColors = { red: { active: 'border-red-600 bg-red-50 text-red-700', inactive: 'border-slate-200 text-slate-500 hover:border-slate-300' }, pink: { active: 'border-pink-600 bg-pink-50 text-pink-700', inactive: 'border-slate-200 text-slate-500 hover:border-slate-300' } };

    return (
        <Modal title={member?.id ? 'Edit Crew Member' : 'Add Crew Member'} onClose={onClose}>
            <FormField label="Full Name" required><input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="First Last" /></FormField>
            <div className="mb-4 mt-6 first:mt-0 pb-2 border-b border-slate-100">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Skills</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Rate each skill area 1-5 (leave at 0 if not applicable)</p>
                <div className="h-px bg-slate-200 mt-2"></div>
            </div>
            {SKILL_CATEGORIES.map(cat => {
                const currentVal = form[cat.key] || 0;
                const subtitle = currentVal > 0 ? cat.getTitle(currentVal) : '';
                return (
                <FormField key={cat.key} label={`${cat.icon} ${cat.label} Skill${subtitle ? ` — ${subtitle}` : ''}`}>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => set(cat.key, 0)}
                            className={`px-3 py-2.5 rounded-lg text-sm font-bold border-2 transition-all ${currentVal === 0 ? 'border-slate-500 bg-slate-100 text-slate-700' : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}>
                            N/A
                        </button>
                        {SKILL_LEVELS.map(lvl => (
                            <button key={lvl.value} type="button" onClick={() => set(cat.key, lvl.value)}
                                className={`flex-1 py-2.5 rounded-lg text-sm font-bold border-2 transition-all ${currentVal === lvl.value ? skillColors[cat.color].active : skillColors[cat.color].inactive}`}>
                                {lvl.label}
                            </button>
                        ))}
                    </div>
                </FormField>
                );
            })}
            <FormField label="Phone"><input className={inputCls} value={form.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="615-555-0000" /></FormField>
            {/* Hourly rate — admin-only. Drives $ totals + CSV export on the
                Payroll page. If unset/zero, payroll shows the row but with
                "$ —" so the missing rate is visible to whoever runs payroll. */}
            {canManageCrew && (
                <FormField label="Hourly Rate (admin only)">
                    <div className="flex items-center gap-2">
                        <NumberField
                            value={form.hourly_rate ?? ''}
                            onChange={v => set('hourly_rate', v === '' ? null : Number(v))}
                            step={1}
                            min={0}
                            prefix="$"
                            placeholder="22.00"
                            className="max-w-[160px]"
                            ariaLabel="Hourly rate"
                        />
                        <span className="text-slate-500 text-sm">/ hour</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                        Used by the Payroll page for $ calculation and CSV export. Leave blank if not on the books.
                    </p>
                </FormField>
            )}
            {/* Gets crew pay — admin-only. When false, the Payroll page hides
                the crew-pay-bonus input on jobs this member leads. Owners
                (Julian, Jensen, Jaylen) have this off by default; admin can
                toggle for anyone else who should be excluded. */}
            {canManageCrew && (
                <FormField label="Receives Crew Pay (admin only)">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => set('gets_crew_pay', !(form.gets_crew_pay === false ? false : true))}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${form.gets_crew_pay !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${form.gets_crew_pay !== false ? 'translate-x-6' : 'translate-x-1'}`}/>
                        </button>
                        <span className="text-sm text-slate-600">
                            {form.gets_crew_pay !== false ? 'Eligible for per-leg crew pay bonuses' : 'Excluded from crew pay'}
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                        Toggle off for owners or anyone else who shouldn't see a bonus input on the Payroll page when they lead jobs. Default $25 for 2-guy / $50 for 4-guy crews; manually entered per job.
                    </p>
                </FormField>
            )}
            <FormField label="Active Member">
                <button type="button" onClick={() => set('active', !(form.active === false ? false : true))}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${form.active !== false ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${form.active !== false ? 'translate-x-6' : 'translate-x-1'}`}/>
                </button>
            </FormField>
            {/* Portal login — invite-by-SMS (crew-auth-plan-v1). Only on
                existing members (new members need an id + phone first). */}
            {canManageLogins && member?.id && (
                <FormField label="Crew Portal Login">
                    {member.auth_user_id ? (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true"></span>
                                Login active
                            </span>
                            <span className="text-[11px] text-slate-400">Re-send below to reset their access.</span>
                        </div>
                    ) : (
                        <p className="text-[11px] text-slate-400 mb-1">
                            No portal login yet. Enter their email and we'll text a setup link to {form.phone || 'their phone'}.
                        </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="email"
                            className={inputCls}
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            placeholder="their-email@example.com"
                            autoComplete="off"
                        />
                        <Button
                            variant="secondary"
                            onClick={handleSendInvite}
                            loading={sendingInvite}
                            disabled={!inviteEmail.trim()}
                        >
                            Send Portal Invite
                        </Button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                        They'll set a password and can save it in their browser. Link expires in 7 days.
                    </p>
                </FormField>
            )}
            <FormField label="Display Color">
                {/* Curated swatch grid (form-controls.md "Color"); a color
                    not in the palette shows as the "now" swatch so nobody
                    is silently reassigned. "Custom…" opens the OS wheel.
                    The hex field stays — it's how exact hexes get pasted. */}
                <ColorSwatches value={form.display_color} onChange={hex => set('display_color', hex)} taken={takenColors} className="mb-2" />
                <div className="flex items-center gap-3">
                    <input className={inputCls} value={form.display_color || ''} onChange={e => set('display_color', e.target.value)} placeholder="#ff0000" style={{maxWidth: '140px'}} aria-label="Display color hex" />
                    <span className="text-[10px] text-slate-400">Paste an exact hex, or pick a swatch above</span>
                </div>
            </FormField>
            <div className="flex justify-between mt-6">
                <div className="flex gap-3 flex-wrap">
                    {member?.id && (
                        <Button
                            variant="tertiary"
                            onClick={handleResetPin}
                            loading={resettingPin}
                            title="Clear this crew lead's portal PIN"
                            leftIcon={<span aria-hidden="true">🔑</span>}
                        >
                            Reset Portal PIN
                        </Button>
                    )}
                    {member?.id && member.active !== false && <Button variant="danger" onClick={handleDelete} leftIcon={<IconTrash size={14}/>}>Deactivate</Button>}
                    {member?.id && member.active === false && onPermDelete && <Button variant="danger" leftIcon={<IconTrash size={14}/>} onClick={async () => {
                        if (!(await confirmDialog({ title: `Delete ${member.name}?`, body: `${member.name} and their skill history will be removed from the roster. Past jobs keep their name. This can't be undone.`, confirmLabel: `Delete ${member.name}`, danger: true }))) return;
                        setSaving(true);
                        try { await onPermDelete(member.id); onClose(); } catch(e) { show('Error: '+(e.message||'Unknown error'), 'error'); } finally { setSaving(false); }
                    }}>Permanently Delete</Button>}
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} loading={saving}>{member?.id ? 'Update Member' : 'Add Member'}</Button>
                </div>
            </div>
            {confirmElement}
        </Modal>
    );
}
